/**
 * Invoices, out of the register and into somebody else's system.
 *
 * Two documents come out of the invoice list, and they are not the same shape.
 *
 * The **QuickBooks** one is an import file, so its column names are not ours to
 * choose: QuickBooks matches on the header text, asterisks included, and a sheet
 * with a renamed or reordered column is rejected at upload. {@link
 * QUICKBOOKS_INVOICE_COLUMNS} is that contract, transcribed from the file the
 * accountant actually imports, and the exporter writes every column in it —
 * including the three that are always blank — because a missing column reads to
 * QuickBooks as a malformed file rather than as an omitted field.
 *
 * The **detail** one is ours, for reading and reconciling, and it carries every
 * field the register holds rather than the handful QuickBooks takes.
 *
 * Both are built from {@link InvoiceExportLine}: one row per invoice line, with
 * the header fields and the resolved names already on it. The join is the
 * server's — `GET /invoices/export` — because a browser doing it would have to
 * hold the companies, the groups and the catalogue to look three ids up, which
 * is what the page used to do and what stopped working once the list was paged.
 */

import { upperEntityName } from "./entity-names.js";

/**
 * The QuickBooks invoice-import columns, in the order the importer expects.
 *
 * A leading `*` marks a column QuickBooks requires a value in; it is part of the
 * header text, not a note about it, so it is written out literally.
 *
 * `Location`, `Memo` and `Service Date` are in the file and empty in it. Somasi
 * does not class invoices by location, the memo is a QuickBooks-side annotation
 * nobody fills in here, and the service date is already in the line description
 * for the lines that have one. They stay as columns so the sheet still matches
 * the format, and stay blank so nothing invents a value for them.
 */
export const QUICKBOOKS_INVOICE_COLUMNS = [
  "*InvoiceNo",
  "*Customer",
  "*InvoiceDate",
  "*DueDate",
  "Terms",
  "Location",
  "Memo",
  "Item(Product/Service)",
  "ItemDescription",
  "ItemQuantity",
  "ItemRate",
  "*ItemAmount",
  "Service Date",
] as const;

/** Columns the format carries and Somasi has nothing to put in. */
export const QUICKBOOKS_BLANK_COLUMNS = ["Location", "Memo", "Service Date"] as const;

/**
 * One invoice line with its invoice's header on it, as the export endpoint
 * returns it.
 *
 * Flat rather than nested because both documents are tables: the QuickBooks
 * sheet repeats the header down its lines, and the detail workbook splits the
 * same rows back into two tabs. A nested shape would have to be flattened by
 * each of them.
 *
 * The names — `customer`, `company_name`, `group_name`, `item` — are resolved
 * server-side from the ids the invoice stores. They are reported rather than
 * recomputed here so that the file and the printed invoice cannot disagree about
 * who was billed.
 */
export interface InvoiceExportLine {
  invoice_id: string;
  invoice_number: string;
  invoice_date: string;
  due_date: string | null;
  /** `Astride US Inc. For: AZP INVESTING Ltd.` — see {@link invoiceCustomer}. */
  customer: string;
  company_name: string | null;
  group_name: string | null;
  /** The invoice's own terms, or the house default where it names none. */
  terms: string;
  status: string;
  period_label: string | null;
  /** Null when the caller may not read bank accounts, as for a viewer. */
  bank_account_name: string | null;
  /** The bank holding that account, on the same terms. Printed ahead of it. */
  bank_name: string | null;
  invoice_notes: string | null;
  subtotal: number;
  total: number;
  line_no: number;
  /** The QuickBooks product/service this line posts to. See {@link quickbooksItem}. */
  item: string;
  /** The QuickBooks item as the catalogue stores it, absent where none is set. */
  quickbooks_item: string | null;
  service_code: string | null;
  service_name: string | null;
  description: string;
  quantity: number;
  unit_amount: number;
  amount: number;
  /** `service`, `service_event`, `sundry`, `virrgin`, `manual` or `import`. */
  source_type: string;
}

/**
 * Who the invoice is billed to, as one string.
 *
 * QuickBooks holds the payer and the company the work was done for in a single
 * customer name — `Astride US Inc. For: AZP INVESTING Ltd.` — which is the
 * sub-customer naming its own file uses, so the two halves have to be joined
 * rather than sent as two columns.
 *
 * The halves are {@link resolveBillToParty}'s, which is also what the printed
 * invoice puts in its address block. Sharing that resolution is the point: an
 * invoice whose addressee was overridden prints one name and must export the
 * same one, or the accountant reconciles a file against a PDF that names
 * somebody else.
 */
export function invoiceCustomer(args: BillToParty): string {
  const { name, onBehalfOf } = resolveBillToParty(args);
  return onBehalfOf ? `${name} For: ${onBehalfOf}` : name;
}

export interface BillToParty {
  invoice?: { bill_to_name?: string | null; bill_to_for?: string | null } | null;
  company?: { legal_name?: string | null } | null;
  group?: { name?: string | null } | null;
}

/**
 * The addressee, and the company the charges belong to under it.
 *
 * Lifted out of `resolveInvoiceBillTo` in `invoice-pdf.ts`, which now calls it,
 * so the document and the export resolve the party once. It lives here because
 * `invoice-pdf.ts` is a separate entry point that pulls in the PDF toolchain,
 * and an export has no business loading that to work out a name.
 *
 * An invoice raised to a group *for* one of its companies is addressed to the
 * group with the company under it. One raised straight to a company is addressed
 * to the company and has no second line, unless the operator wrote one — an
 * override changes who is billed, not which company the work was done for.
 */
export function resolveBillToParty({ invoice, company, group }: BillToParty): {
  name: string;
  onBehalfOf: string | null;
} {
  const throughGroup = !!company && !!group;

  // Both halves are a company's or a group's name, so both are written the way
  // those names are written everywhere else — see {@link upperEntityName}. It is
  // applied here as well as on the register's write path because this is also
  // where the operator's own bill-to override is resolved, and that one is typed
  // free-hand into the invoice rather than taken from a stored name.
  return {
    name:
      upperEntityName(invoice?.bill_to_name) ||
      upperEntityName(throughGroup ? group?.name : company?.legal_name) ||
      upperEntityName(group?.name) ||
      "CLIENT",
    onBehalfOf:
      upperEntityName(invoice?.bill_to_for) ??
      (throughGroup ? upperEntityName(company?.legal_name) : null),
  };
}

/**
 * What a line posts to in QuickBooks.
 *
 * `quickbooks_item` on the service is the authority, because it is the only
 * field that holds QuickBooks' own `Category:Name` path — `Incorporation:Formation
 * Service Fee` — and that path is what the importer matches on. Where a service
 * has not been mapped yet, its own name goes out instead, and where the line is
 * not a service at all — a disbursement, a VIRRGIN charge, a typed-in line — the
 * name is whatever the source record was titled.
 *
 * None of those fallbacks will match an existing QuickBooks item, so importing
 * them creates one. That is deliberate and it is the accountant's call to make:
 * the alternative is blanking the column, which posts the line to a default
 * account with nothing on the row saying it did.
 */
export function quickbooksItem(args: {
  service?: { quickbooks_item?: string | null; name?: string | null } | null;
  /** The source record's own title, for a line with no service behind it. */
  sourceTitle?: string | null;
  /** The line's description, the last thing left to name it by. */
  description: string;
}): string {
  return (
    args.service?.quickbooks_item?.trim() ||
    args.service?.name?.trim() ||
    args.sourceTitle?.trim() ||
    args.description.trim()
  );
}

/** Lines whose `item` is a guess rather than a mapped QuickBooks path. */
export function unmappedQuickbooksLines(rows: InvoiceExportLine[]): InvoiceExportLine[] {
  return rows.filter((row) => !row.quickbooks_item?.trim());
}

/**
 * A `YYYY-MM-DD` date as the number a spreadsheet stores a date as: whole days
 * since 1899-12-30, the epoch Excel's leap-year bug implies. Null for anything
 * that is not a plain calendar date, including the empty string.
 *
 * Done by arithmetic rather than through a `Date` because the spreadsheet writer
 * converts a `Date` in the *local* zone, which lands the cell a fraction of a day
 * either side of midnight for everyone not on UTC — 46185.229 where the file is
 * supposed to say 46185. This is on the calendar date alone, so the serial is the
 * same number wherever the export is taken from.
 *
 * It matters because the date column is the one thing in these files another
 * system parses rather than displays: QuickBooks reads a typed date cell, and a
 * date written as text is read through whatever locale the machine is set to.
 */
export function excelSerial(date: string): number | null {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date.trim());
  if (!parts) return null;
  const [, year, month, day] = parts;
  return Date.UTC(Number(year), Number(month) - 1, Number(day)) / 86_400_000 + 25_569;
}
